import React, { useEffect, useState } from 'react'
import { useLocation } from 'wouter'
import { Globe, ArrowRight, CheckCircle2, PencilLine } from 'lucide-react'
import {
  PageContentService,
  WebsiteDefaultsService,
  type PageManifestEntry,
  type PageStatus,
} from '@/services/pageContent'

interface Row extends PageManifestEntry {
  status?: PageStatus
}

const WebsiteContent: React.FC = () => {
  const [, setLocation] = useLocation()
  const [rows, setRows] = useState<Row[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    load()
  }, [])

  const load = async () => {
    setIsLoading(true)
    setError('')
    try {
      const [pages, statuses] = await Promise.all([
        WebsiteDefaultsService.listPages(),
        PageContentService.listStatus(),
      ])
      const statusBySlug = new Map(statuses.map((s) => [s.slug, s]))
      setRows(pages.map((p) => ({ ...p, status: statusBySlug.get(p.slug) })))
    } catch (err: any) {
      console.error('Failed to load website content list:', err)
      setError(
        err?.message?.includes('page list')
          ? 'Could not reach the public website to list its pages. Is it deployed and reachable?'
          : 'Failed to load the website content list. Please try again.',
      )
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          <span className="text-gray-600">Loading pages...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Globe className="h-8 w-8 text-primary shrink-0" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Website Content</h1>
          <p className="text-gray-600">
            Edit the public marketing site's page copy. Changes are drafted, previewed live, then
            published when you're ready.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((row) => {
          const hasUnpublishedDraft =
            row.status?.hasDraft &&
            (!row.status.publishedAt || new Date(row.status.updatedAt) > new Date(row.status.publishedAt))
          return (
            <button
              key={row.slug}
              onClick={() => setLocation(`/website-content/${row.slug}`)}
              className="text-left bg-white border border-gray-200 rounded-lg p-5 flex flex-col gap-3 hover:border-primary/40 transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-gray-900">{row.label}</h3>
                  <p className="text-xs text-gray-400 mt-0.5">/{row.slug === 'home' ? '' : row.slug}</p>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-300 shrink-0" />
              </div>

              <div className="mt-auto flex items-center gap-1.5 text-xs">
                {row.status?.publishedAt ? (
                  <>
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    <span className="text-green-700">Published</span>
                  </>
                ) : (
                  <span className="text-gray-400">{'Not edited yet — showing site default'}</span>
                )}
                {hasUnpublishedDraft && (
                  <span className="ml-1 flex items-center gap-1 text-amber-600">
                    <PencilLine className="h-3.5 w-3.5" /> unpublished edits
                  </span>
                )}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default WebsiteContent
